// SuperAdmin vendor-account provisioning: create a vendor, grant the vendor bundle to an
// existing account (found by phone), and mint per-vendor Passport clients.
// Talks to the Laravel /api/admin endpoints via this.$axios (adminAxios, Bearer).
export default {
    data: () => ({
        // --- create panel ---
        createForm: { name: '', email: '', password: '', tel: '', client_name: '' },
        createErrors: {},
        creating: false,

        // --- grant / lookup panel ---
        searchTel: '',
        candidates: [],
        searched: false,
        searching: false,
        grantingId: null,
        generatingId: null,

        // --- show-once secret dialog ---
        secretDialog: false,
        secretResult: { title: '', email: null, username: null, password: null, client_id: '', client_secret: '' },
        copied: false,
        // password suggestion: slug(name)@<uuid seg4>-<uuid seg5>, like the create seeder
        passwordSuffix: '',
        passwordAuto: true,
        // email suggestion: slug(name)@client.hal-logistics.la (create-seeder pattern).
        // NOT <tel>@email.generated.com — that collides with the phone-registered account.
        emailAuto: true,
    }),

    computed: {
        createValid() {
            const f = this.createForm;
            return !!(f.name && f.email && f.password && f.tel);
        },

        // Paste-ready OAuth password-grant body for POST /oauth/token.
        // client_id stays a number. The real password is embedded when known (create
        // flow, show-once); the generate-client flow has no password → placeholder.
        tokenRequestJson() {
            return JSON.stringify({
                grant_type: 'password',
                client_id: this.secretResult.client_id,
                client_secret: this.secretResult.client_secret,
                scope: '*',
                username: this.secretResult.username || 'YOUR-VENDOR-USERNAME',
                password: this.secretResult.password || 'YOUR-LOGIN-APP-PASSWORD',
            }, null, 4);
        },
    },

    created() {
        this.passwordSuffix = this.newPasswordSuffix();
    },

    watch: {
        'createForm.name'() {
            // Suggest email + password from the vendor name until the admin edits them.
            if (this.emailAuto) {
                this.createForm.email = this.suggestedEmail();
            }
            if (this.passwordAuto) {
                this.createForm.password = this.suggestedPassword();
            }
        },
    },

    methods: {
        copyText(text) {
            if (text && navigator.clipboard) {
                navigator.clipboard.writeText(String(text)).catch(() => {});
            }
        },

        copyTokenRequest() {
            this.copyText(this.tokenRequestJson);
            this.copied = true;
            setTimeout(() => { this.copied = false; }, 1500);
        },

        genUuid() {
            if (typeof crypto !== 'undefined' && crypto.randomUUID) {
                return crypto.randomUUID();
            }
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
                const r = (Math.random() * 16) | 0;
                const v = c === 'x' ? r : ((r & 0x3) | 0x8);
                return v.toString(16);
            });
        },

        newPasswordSuffix() {
            // last two UUID segments, e.g. "a716-446655440000"
            return this.genUuid().split('-').slice(-2).join('-');
        },

        slugName(name) {
            return String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
        },

        suggestedEmail() {
            const slug = this.slugName(this.createForm.name);
            return slug ? slug + '@client.hal-logistics.la' : '';
        },

        suggestedPassword() {
            const slug = this.slugName(this.createForm.name) || 'vendor';
            return slug + '@' + this.passwordSuffix;
        },

        regeneratePassword() {
            this.passwordSuffix = this.newPasswordSuffix();
            this.passwordAuto = true;
            this.createForm.password = this.suggestedPassword();
        },

        notifySuccess() {
            this.$notification.OpenNotification_AddItem_OnSuccess('top-right', 'dark', 3000);
        },

        notifyError(message) {
            this.$notification.OpenNotification_Error('top-right', 'danger', 3000, message || 'ດຳເນີນການບໍ່ສຳເລັດ');
        },

        createVendor() {
            if (this.creating) {
                return;
            }
            this.createErrors = {};
            if (!this.createValid) {
                return;
            }
            this.creating = true;
            this.$axios.post('create-vendor', this.createForm).then((res) => {
                this.creating = false;
                const d = res.data;
                this.secretResult = {
                    title: 'ສ້າງ Vendor ສຳເລັດ',
                    email: d.user.email,
                    username: d.user.email,
                    password: this.createForm.password,
                    client_id: d.client_id,
                    client_secret: d.client_secret,
                };
                this.secretDialog = true;
                this.createForm = { name: '', email: '', password: '', tel: '', client_name: '' };
                this.notifySuccess();
            }).catch((err) => {
                this.creating = false;
                if (err.response && err.response.status === 422) {
                    const data = err.response.data || {};
                    const obj = data.errors || {};
                    if (Object.keys(obj).length) {
                        for (const [key, value] of Object.entries(obj)) {
                            this.$set(this.createErrors, key, value[0]);
                        }
                    } else {
                        this.notifyError(data.message || 'ຂໍ້ມູນບໍ່ຖືກຕ້ອງ');
                    }
                } else {
                    this.notifyError('ສ້າງ Vendor ບໍ່ສຳເລັດ');
                }
            });
        },

        lookup() {
            if (!this.searchTel) {
                return;
            }
            this.searching = true;
            this.candidates = [];
            this.$axios.get('vendor-lookup', { params: { tel: this.searchTel } }).then((res) => {
                this.searching = false;
                this.searched = true;
                this.candidates = res.data.data || [];
            }).catch(() => {
                this.searching = false;
                this.searched = true;
                this.notifyError('ຄົ້ນຫາບໍ່ສຳເລັດ');
            });
        },

        grant(candidate) {
            if (this.grantingId === candidate.user_id) {
                return;
            }
            this.grantingId = candidate.user_id;
            this.$axios.post('grant-vendor', { user_id: candidate.user_id }).then((res) => {
                this.grantingId = null;
                candidate.is_vendor = true;
                this.$set(candidate, 'customer_missing', !!res.data.customer_missing);
                this.notifySuccess();
            }).catch(() => {
                this.grantingId = null;
                this.notifyError('ໃຫ້ສິດ Vendor ບໍ່ສຳເລັດ');
            });
        },

        generateClient(candidate) {
            if (this.generatingId === candidate.user_id) {
                return;
            }
            this.generatingId = candidate.user_id;
            this.$axios.post('generate-vendor-client', { user_id: candidate.user_id }).then((res) => {
                this.generatingId = null;
                const d = res.data;
                this.$set(candidate, 'owned_client_count', d.owned_client_count);
                this.secretResult = {
                    title: 'ສ້າງ API Client ສຳເລັດ',
                    email: candidate.email,
                    username: candidate.email,
                    password: null,
                    client_id: d.client_id,
                    client_secret: d.client_secret,
                };
                this.secretDialog = true;
                this.notifySuccess();
            }).catch((err) => {
                this.generatingId = null;
                const msg = (err.response && err.response.data && err.response.data.message)
                    ? err.response.data.message
                    : 'ສ້າງ Client ບໍ່ສຳເລັດ';
                this.notifyError(msg);
            });
        },
    },
};
